import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { TextInput } from '@gravity-ui/uikit';

import { DOING, DOMEN, SATISFACTION } from '../../../constants';

import styles from './styles.module.scss';
import { ResultScoreSelect } from './result-score-select';
import { MKFCodeSelector } from './mkf-code-selector';

interface ProblemAssessmentFormProps {
	index: number;
	problemId?: number;
	problemText: string;
	problemImportance: number;
	shortTermGoals?: any[];
	isMobile: any;
	onGoalScoreChange?: (goalIndex: number, score: string) => void;
}

interface ProblemFields {
	execution: number | '';
	satisfaction: number | '';
	prefix: string[];
	domainCode: string[];
	shortTermGoals?: { resultScore?: string }[];
}

interface StepThreeFormData {
	problems: ProblemFields[];
	goal: string;
	goalPlan: string;
}

export const ProblemAssessmentForm = ({
	index,
	problemText,
	problemImportance,
	shortTermGoals = [],
	onGoalScoreChange,
}: ProblemAssessmentFormProps) => {
	const {
		register,
		control,
		formState: { isSubmitted, errors },
	} = useFormContext<StepThreeFormData>();

	// Получаем shortTermGoals из формы
	const formShortTermGoals = useWatch({
		control,
		name: `problems.${index}.shortTermGoals`,
	}) as { resultScore?: string }[] | undefined;

	return (
		<div className={`${styles.block} ${styles.problem_description}`}>
			<div className={styles.block__problem}>
				<h3>{problemImportance}.</h3>
				<p className={styles.block__problem__problem_text}>{problemText}</p>
			</div>
			<div className={styles.block__rows}>
				<FormRow label={DOING} className={`${styles.row} ${styles.short}`} required>
					<TextInput
						size="xl"
						hasClear
						type="text"
						{...register(`problems.${index}.execution`, { required: true })}
						validationState={isSubmitted && errors.problems?.[index]?.execution ? 'invalid' : undefined}
						errorPlacement="inside"
						errorMessage={
							isSubmitted && errors.problems?.[index]?.execution
								? 'Обязательно для заполнения'
								: undefined
						}
					/>
				</FormRow>
				<FormRow label={SATISFACTION} className={`${styles.row} ${styles.short}`} required>
					<TextInput
						size="xl"
						hasClear
						type="text"
						{...register(`problems.${index}.satisfaction`, { required: true })}
						validationState={isSubmitted && errors.problems?.[index]?.satisfaction ? 'invalid' : undefined}
						errorPlacement="inside"
						errorMessage={
							isSubmitted && errors.problems?.[index]?.satisfaction
								? 'Обязательно для заполнения'
								: undefined
						}
					/>
				</FormRow>

				<FormRow label={DOMEN} className={`${styles.row} ${styles.mkf} ${styles.domen}`}>
					<Controller
						name={`problems.${index}.domainCode`}
						control={control}
						render={({ field: domainField }) => (
							<Controller
								name={`problems.${index}.prefix`}
								control={control}
								render={({ field: prefixField }) => (
									<MKFCodeSelector
										prefix={prefixField.value}
										domainCode={domainField.value}
										onChange={(prefix, domainCode) => {
											prefixField.onChange(prefix);
											domainField.onChange(domainCode);
										}}
										validationState={
											isSubmitted && errors.problems?.[index]?.domainCode ? 'invalid' : undefined
										}
										errorMessage={
											isSubmitted && errors.problems?.[index]?.domainCode
												? 'Обязательно для заполнения'
												: undefined
										}
									/>
								)}
							/>
						)}
					/>
				</FormRow>

				{shortTermGoals && shortTermGoals.length > 0 && (
					<div className={styles.shortTermGoalsSection}>
						<h4>Краткосрочная цель</h4>
						<div className={styles.shortTermGoalsDisplay}>
							{shortTermGoals.map((goal: any, goalIndex: number) => {
								return goal.goal ? (
									<div key={goal?.id || goalIndex} className={styles.goalRow}>
										<TextInput value={goal?.goal} readOnly size="xl" className={styles.goalInput} />
										<Controller
											name={`problems.${index}.shortTermGoals.${goalIndex}.resultScore` as any}
											control={control}
											defaultValue=""
											render={({ field }) => (
												<ResultScoreSelect
													value={field.value as string}
													placeholder="Оценка"
													onChange={(score) => {
														field.onChange(score);
														onGoalScoreChange?.(goalIndex, score);
													}}
												/>
											)}
										/>
									</div>
								) : null;
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
